import { getRouteApi, Link } from '@tanstack/react-router';
import { Clock } from 'lucide-react';
import { useId, useMemo, useState, type FormEvent } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando, ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { janelaAteHoje } from '@/lib/dia-da-operacao';
import { formatarData, formatarDataHora, formatarDecimal, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import type { DivergenciaDaApi } from './api';
import {
  RESOLUCOES_ESCOLHIVEIS,
  TIPOS_DE_DIVERGENCIA,
  resumirFila,
  rotuloDaResolucao,
  rotuloDoTipo,
  type ResolucaoEscolhivel,
  type TipoDeDivergencia,
} from './conciliacao';
import {
  explicarFalha,
  useApurar,
  useDivergencias,
  useGravarTolerancia,
  useAtribuirResponsavel,
  useCadastrosDaOperacao,
  useResolver,
  useTolerancia,
} from './consultas';
import { usePaginacao } from './paginacao';
import { contar, fraseDoErro } from './texto';

/**
 * OPE 03 — a fila de divergências entre o horímetro, o apontamento e a hora
 * que o cliente reconheceu.
 *
 * A tela não recalcula nada: a conta é do banco (`oper.divergencia_apurada`),
 * e a fila é o REGISTRO dela, com quem tratou e como. O que a tela faz é
 * deixar tratar — ver os quatro números lado a lado, escolher o que vale e
 * escrever por quê.
 *
 * O filtro vive na URL (p.16): "me manda as divergências abertas de maio" é um
 * link, e não uma sequência de cliques.
 */
const rota = getRouteApi('/operacao/conciliacao');

/* Os últimos 30 dias até hoje, no fuso da operação (`janelaAteHoje`). */
function periodoPadrao(): { de: string; ate: string } {
  return janelaAteHoje(30);
}

/* Uma data que não existe (um link com "?de=2026-13-01" passa pela forma) sai
   como veio, em vez de derrubar a tela no chip do filtro. */
const diaDe = (dia: string) => {
  const data = new Date(`${dia}T12:00:00`);
  return Number.isNaN(data.getTime()) ? dia : formatarData(data);
};
/* Número e unidade separados, a unidade em cinza (p.12) — como nas tabelas. */
const horas = (valor: number | null) =>
  valor === null ? '—' : <Medida valor={formatarDecimal(valor, 2)} unidade="h" />;

/** O sinal escrito: "+2,00 h" diz de que lado está a diferença sem depender de cor. */
function diferenca(valor: number): string {
  return `${valor > 0 ? '+' : valor < 0 ? '−' : ''}${formatarDecimal(Math.abs(valor), 2)}`;
}

const COLUNAS: readonly Coluna<DivergenciaDaApi>[] = [
  {
    chave: 'maquina',
    rotulo: 'Máquina e dia',
    celula: (d) => (
      <span className="flex flex-col">
        {/* Código em monoespaçada (p.18). */}
        <span className="codigo text-tinta">{d.equipamento}</span>
        <span className="txt-apoio text-tinta-secundaria">{diaDe(d.dia)}</span>
      </span>
    ),
  },
  {
    chave: 'tipo',
    rotulo: 'Divergência',
    celula: (d) => <span className="txt-corpo text-tinta">{rotuloDoTipo(d.tipo)}</span>,
  },
  {
    chave: 'diferenca',
    rotulo: 'Diferença',
    historia: 'NUM-OPR-08',
    alinhamento: 'numero',
    celula: (d) => <Medida valor={diferenca(d.diferencaH)} unidade="h" />,
  },
  {
    /* QUEM RESPONDE, na lista e nao so no painel: a fila existe para alguem
       abrir de manha e saber quais sao suas, e descobrir isso abrindo uma por
       uma e o oposto de uma fila. Secundaria (R01, p.28): sai em M e no
       cartao, onde a identidade e a diferenca mandam mais. */
    chave: 'responsavel',
    rotulo: 'Responsável',
    prioridade: 'secundaria',
    celula: (d) =>
      d.responsavel === null ? (
        <span className="txt-apoio text-tinta-secundaria">Ninguém ainda</span>
      ) : (
        <TextoDaCelula texto={d.responsavel} classe="txt-corpo text-tinta" linhas={1} />
      ),
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    celula: (d) => (
      <span className="flex flex-col">
        <span className="txt-corpo text-tinta">{d.situacao === 'aberta' ? 'Aberta' : 'Resolvida'}</span>
        {d.situacao === 'resolvida' ? (
          <span className="txt-apoio text-tinta-secundaria">{rotuloDaResolucao(d.resolucao)}</span>
        ) : null}
      </span>
    ),
  },
];

export function PaginaConciliacaoDeHoras() {
  const base = useId();
  const busca = rota.useSearch();
  const navegar = rota.useNavigate();
  const padrao = useMemo(periodoPadrao, []);
  const de = busca.de ?? padrao.de;
  const ate = busca.ate ?? padrao.ate;
  const filtro = useMemo(
    () => ({ de, ate, ...(busca.situacao ? { situacao: busca.situacao } : {}) }),
    [de, ate, busca.situacao],
  );

  const fila = useDivergencias(filtro);
  const tolerancia = useTolerancia();
  const apurar = useApurar();
  /* Reapurar, atribuir e resolver são de quem confere apontamento (prova do
     marco, 07/10/2026): quem só lê a operação vê a fila, sem os botões. */
  const sessao = useSessao();
  const podeConferir = (sessao.data?.permissoes ?? []).includes('apontamento.conferir');
  const [aviso, setAviso] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);
  const [selecionada, setSelecionada] = useState<string | null>(null);

  const linhas = useMemo(() => fila.data?.divergencias ?? [], [fila.data]);
  const total = fila.data?.total ?? linhas.length;
  const resumo = resumirFila(linhas);
  const aberta = linhas.find((d) => d.id === selecionada) ?? null;
  const { visiveis, rodape } = usePaginacao(linhas);

  /* Filtro aplicado muda de aparência e ganha "✕" (p.16). O período sempre
     vale — o que conta como filtro é ele ter saído dos últimos 30 dias. */
  const periodoAlterado = de !== padrao.de || ate !== padrao.ate;
  const chips: readonly ChipFiltro[] = [
    ...(periodoAlterado
      ? [
          {
            id: 'periodo',
            rotulo: `Período: ${diaDe(de)} a ${diaDe(ate)}`,
            aoRemover: () => {
              filtrar({ de: '', ate: '' });
            },
          },
        ]
      : []),
    ...(busca.situacao
      ? [
          {
            id: 'situacao',
            rotulo: `Situação: ${busca.situacao === 'aberta' ? 'Abertas' : 'Resolvidas'}`,
            aoRemover: () => {
              filtrar({ situacao: '' });
            },
          },
        ]
      : []),
  ];

  function limparFiltros() {
    void navegar({ search: {} });
  }

  function filtrar(mudanca: { de?: string; ate?: string; situacao?: '' | 'aberta' | 'resolvida' }) {
    const proximo: { de: string; ate: string; situacao: '' | 'aberta' | 'resolvida' } = {
      de,
      ate,
      situacao: busca.situacao ?? '',
      ...mudanca,
    };
    void navegar({
      search: {
        ...(proximo.de ? { de: proximo.de } : {}),
        ...(proximo.ate ? { ate: proximo.ate } : {}),
        ...(proximo.situacao ? { situacao: proximo.situacao } : {}),
      },
    });
  }

  function reapurar() {
    setAviso(null);
    setRecusa(null);
    apurar.mutate(
      { de, ate },
      {
        onSuccess: (r) => {
          setAviso(
            r.divergenciasAbertas === 0
              ? `Período de ${diaDe(de)} a ${diaDe(ate)} reapurado. Nenhuma divergência aberta.`
              : `Período de ${diaDe(de)} a ${diaDe(ate)} reapurado. ${contar(r.divergenciasAbertas, 'divergência aberta', 'divergências abertas')}.`,
          );
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Operação"
            titulo="Conciliação de horas"
            subtitulo="Horímetro, apontamento e hora reconhecida pelo cliente, lado a lado"
            acoes={
              /* Sem laranja no cabeçalho: a ação que esta tela existe para
                 provocar é RESOLVER, e ela mora no painel da divergência. */
              <>
                <Link to="/operacao/importacoes" className="botao botao-secundario">
                  Ver importações
                </Link>
                {podeConferir ? (
                  <Botao nivel="secundario" onClick={reapurar} disabled={apurar.isPending}>
                    {apurar.isPending ? 'Reapurando…' : 'Reapurar período'}
                  </Botao>
                ) : null}
              </>
            }
          />

          <FaixaIndicadores>
            {/* A TOLERÂNCIA NÃO É INDICADOR (p.17): é parâmetro da empresa, e
                ninguém pergunta por ela de manhã. Ela entra como contexto de
                quem ela decide — as divergências — e mora por extenso no bloco
                "Tolerância da conciliação", abaixo da fila. */}
            {/* Carregando (p.32): o número vira esqueleto — antes, "0" e "0,0 h"
                no lugar de uma fila que ainda não tinha chegado. */}
            <CardIndicador
              rotulo="Divergências abertas"
              carregando={fila.isPending}
              valor={formatarInteiro(resumo.abertas)}
              contexto={
                /* Vazio enquanto a fila vem: o card desenha a barra (p.32). */
                fila.isPending
                  ? ''
                  : tolerancia.data === undefined
                    ? `de ${formatarInteiro(linhas.length)} no período e na situação escolhidos`
                    : `de ${formatarInteiro(linhas.length)} no período e na situação escolhidos; diferença acima de ${formatarDecimal(tolerancia.data, 2)} h`
              }
              icone={Clock}
              /* R02: o BLOCO diz o assunto e não muda com o valor — o mesmo
                 assunto não troca de identidade quando zera. O NÚMERO é que é
                 o alerta: divergência aberta é hora que ninguém explicou
                 ainda, em vermelho; zero sai em preto. */
              tom="parada"
              alerta={resumo.abertas > 0}
              {...(linhas.length > 0 ? { proporcao: resumo.abertas / linhas.length } : {})}
            />
            <CardIndicador
              rotulo="Horas a explicar"
              carregando={fila.isPending}
              valor={formatarDecimal(resumo.horasEmAberto, 1)}
              unidade="h"
              contexto="soma das diferenças abertas, sem compensar uma pela outra"
              icone={Clock}
              tom="standby"
            />
          </FaixaIndicadores>

          {/* O filtro já ia para a URL; faltava o resto da p.16: o filtro
              aplicado muda de aparência e ganha "✕", a lista filtrada mostra a
              contagem, e "Limpar filtros" aparece quando há o que limpar. */}
          <BarraFiltros
            chips={chips}
            /* Enquanto a fila vem, a contagem vira barra e quem diz que carrega
               é a tabela (p.32). */
            contagem={fila.isPending ? null : contar(total, 'divergência', 'divergências')}
            aoLimpar={limparFiltros}
          >
            <Campo id={`${base}-de`} rotulo="De">
              <input
                id={`${base}-de`}
                type="date"
                className={`controle ${periodoAlterado ? 'border-marca bg-marca-fundo' : ''}`}
                value={de}
                onChange={(e) => {
                  filtrar({ de: e.target.value });
                }}
              />
            </Campo>
            <Campo id={`${base}-ate`} rotulo="Até">
              <input
                id={`${base}-ate`}
                type="date"
                className={`controle ${periodoAlterado ? 'border-marca bg-marca-fundo' : ''}`}
                value={ate}
                onChange={(e) => {
                  filtrar({ ate: e.target.value });
                }}
              />
            </Campo>
            <CampoSelecao
              rotulo="Situação"
              valor={busca.situacao ?? ''}
              rotuloVazio="Todas"
              aplicado={Boolean(busca.situacao)}
              opcoes={[
                { valor: 'aberta', rotulo: 'Abertas' },
                { valor: 'resolvida', rotulo: 'Resolvidas' },
              ]}
              aoMudar={(valor) => {
                filtrar({ situacao: valor as '' | 'aberta' | 'resolvida' });
              }}
            />
          </BarraFiltros>

          {aviso === null ? null : (
            <p role="status" className="txt-corpo text-tinta">
              {aviso}
            </p>
          )}
          {recusa === null ? null : (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
              <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
              <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
            </div>
          )}

          <Tabela
            rotulo="Divergências"
            colunas={COLUNAS}
            linhas={visiveis}
            alturaDupla
            chaveDaLinha={(d) => d.id}
            selecionada={selecionada}
            aoSelecionar={(d) => {
              setSelecionada(d.id);
            }}
            carregando={fila.isPending}
            erro={
              fila.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar a fila"
                  descricao={explicarFalha(fila.error).comoResolver}
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void fila.refetch();
                      }}
                    >
                      Carregar de novo
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              chips.length > 0 ? (
                <EstadoVazio
                  titulo="Nenhuma divergência com esses filtros"
                  descricao="Troque o período ou a situação, ou limpe os filtros para ver os últimos 30 dias."
                  acao={
                    <Botao nivel="secundario" onClick={limparFiltros}>
                      Limpar filtros
                    </Botao>
                  }
                />
              ) : (
                <EstadoVazio
                  titulo="Nenhuma divergência no período"
                  descricao="A fila nasce quando um arquivo de horas reconhecidas é promovido. Importe o arquivo do cliente para comparar com o apontamento."
                  acao={
                    <Link to="/operacao/importacoes" className="botao botao-secundario">
                      Ir para importações
                    </Link>
                  }
                />
              )
            }
            rodape={
              /* RODAPÉ DA p.18, e o aviso do teto DENTRO da margem dele: a rota
                 devolve no máximo as primeiras do período, e quem vê a última
                 página precisa saber que existem outras. */
              <>
                <RodapeTabela {...rodape} />
                {total > linhas.length ? (
                  <p className="txt-apoio border-t border-borda-sutil bg-rebaixada px-4 py-2 text-tinta-secundaria">
                    {`O período tem ${formatarInteiro(total)} divergências; a lista traz as ${formatarInteiro(linhas.length)} primeiras. Estreite o período para ver as outras.`}
                  </p>
                ) : null}
              </>
            }
          />

          <ToleranciaDaEmpresa atual={tolerancia.data ?? null} />
        </div>
      </div>

      {aberta ? (
        <Detalhe
          key={aberta.id}
          divergencia={aberta}
          aoFechar={() => {
            setSelecionada(null);
          }}
        />
      ) : null}
    </>
  );
}

function Detalhe({ divergencia: d, aoFechar }: { readonly divergencia: DivergenciaDaApi; readonly aoFechar: () => void }) {
  const base = useId();
  const resolver = useResolver();
  const atribuir = useAtribuirResponsavel();
  const sessao = useSessao();
  const podeConferir = (sessao.data?.permissoes ?? []).includes('apontamento.conferir');
  /* A MESMA consulta que o formulario de apontamento usa (`/operacao/cadastros`),
     e nao uma rota nova: ela exige `operacao.ler`, que quem confere apontamento
     tem, e ja devolve as pessoas ativas da empresa. */
  const pessoas = useCadastrosDaOperacao();
  const [resolucao, setResolucao] = useState<'' | ResolucaoEscolhivel>('');
  const [justificativa, setJustificativa] = useState('');
  const [evidencia, setEvidencia] = useState('');
  const [erros, setErros] = useState<Readonly<Record<'resolucao' | 'justificativa', string | null>>>({
    resolucao: null,
    justificativa: null,
  });
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);
  const eu = sessao.data?.usuarioId ?? null;
  function atribuirA(responsavelId: string) {
    setRecusa(null);
    atribuir.mutate(
      { id: d.id, responsavelId: responsavelId === '' ? null : responsavelId },
      { onError: (falha) => { setRecusa(explicarFalha(falha)); } },
    );
  }
  const podeAssumir = d.responsavelId === null && eu !== null && (pessoas.data?.operadores ?? []).some((p) => p.id === eu);
  const explicacao = d.tipo in TIPOS_DE_DIVERGENCIA ? TIPOS_DE_DIVERGENCIA[d.tipo as TipoDeDivergencia].explicacao : '';

  /* As mesmas regras de antes, uma por campo — validadas ao SAIR do campo
     (p.16), e não só no envio. */
  const mensagemDaResolucao = (valor: string) => (valor === '' ? 'Escolha o que vale para este dia.' : null);
  const mensagemDaJustificativa = (valor: string) =>
    valor.trim().length < 10 ? 'Explique a resolução em pelo menos uma frase.' : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = {
      resolucao: mensagemDaResolucao(resolucao),
      justificativa: mensagemDaJustificativa(justificativa),
    };
    setErros(achados);
    /* O foco vai ao primeiro campo recusado. */
    if (achados.resolucao !== null) document.getElementById(`${base}-resolucao`)?.focus();
    else if (achados.justificativa !== null) document.getElementById(`${base}-justificativa`)?.focus();
    if (achados.resolucao !== null || achados.justificativa !== null || resolucao === '') return;
    setRecusa(null);
    resolver.mutate(
      { id: d.id, envio: { resolucao, justificativa: justificativa.trim(), evidencia: evidencia.trim() || null } },
      {
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const tratamento =
    d.situacao === 'resolvida' ? (
      <div className="flex flex-col gap-2">
        <DadoChave rotulo="Resolução">{rotuloDaResolucao(d.resolucao)}</DadoChave>
        <DadoChave rotulo="Por">{d.resolvidaPor ?? 'Apuração automática'}</DadoChave>
        {d.resolvidaEm ? <DadoChave rotulo="Em">{formatarDataHora(new Date(d.resolvidaEm))}</DadoChave> : null}
        {d.evidencia ? <DadoChave rotulo="Evidência">{d.evidencia}</DadoChave> : null}
        <p className="txt-corpo text-pretty text-tinta">{d.justificativa}</p>
      </div>
    ) : !podeConferir ? (
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        Quem atribui e resolve a divergência é o perfil Gestor operacional, que confere o apontamento.
      </p>
    ) : (
      /* O PAINEL ABRE EXPANDIDO (31/10/2026): o formulário usa a largura, em
         grade — responsável, o que vale e a evidência lado a lado, o porquê na
         linha inteira. No painel estreito e no celular, uma coluna. */
      <form id={`${base}-form`} onSubmit={enviar} className="grade-do-painel">
        {/* ATRIBUIR VEM ANTES DE RESOLVER, e e por isso que ele esta aqui em
            cima: a divergencia passa por alguem antes de ser explicada, e quem
            abre a fila de manha precisa saber quais sao suas. Ele grava SOZINHO
            ao escolher, fora do `submit` do formulario — atribuir nao exige
            justificativa, e exigir resolver junto deixaria a fila sem dono
            enquanto ninguem soubesse a resposta. */}
        <HistoriaNaMesa codigo="CMP-OPR-09" className="min-w-0">
          <Campo
            id={`${base}-responsavel`}
            rotulo="Responsável"
            ajuda="Quem vai explicar esta diferença. Pode ficar em branco até alguém assumir."
            opcional
            /* ASSUMIR NUM CLIQUE, ao lado do rótulo: quem abre a fila de manhã
               e vai explicar a diferença não procura o próprio nome na lista. */
            {...(podeAssumir
              ? {
                  etiqueta: (
                    <Botao nivel="fantasma" tamanho="p" disabled={atribuir.isPending} onClick={() => { atribuirA(eu ?? ''); }}>
                      Assumir divergência
                    </Botao>
                  ),
                }
              : {})}
          >
            {/* As pessoas ainda vêm (p.32): o controle vira barra, e não uma
                opção escrita "Carregando" que se podia escolher. */}
            {pessoas.isPending ? (
              <ControleCarregando oQue="Carregando as pessoas" />
            ) : (
              /* A lista nativa continua (o doctor H34 cobra o seletor ligado ao
                 responsável gravado); quem vai explicar a diferença se atribui
                 pelo "Assumir divergência", ao lado do rótulo. */
              <select
                id={`${base}-responsavel`}
                className="controle w-full"
                value={d.responsavelId ?? ''}
                disabled={atribuir.isPending}
                onChange={(e) => {
                  atribuirA(e.target.value);
                }}
              >
                <option value="">Ninguém ainda</option>
                {(pessoas.data?.operadores ?? []).map((pessoa) => (
                  <option key={pessoa.id} value={pessoa.id}>
                    {pessoa.nome}
                  </option>
                ))}
              </select>
            )}
          </Campo>
        </HistoriaNaMesa>

        <Campo id={`${base}-resolucao`} rotulo="O que vale" erro={erros.resolucao}>
          <ListaDeEscolha
            id={`${base}-resolucao`}
            rotulo="O que vale"
            valor={resolucao}
            aoMudar={(valor) => {
              setResolucao(valor as '' | ResolucaoEscolhivel);
              setErros((atual) => ({ ...atual, resolucao: null }));
            }}
            aoSair={() => {
              setErros((atual) => ({ ...atual, resolucao: mensagemDaResolucao(resolucao) }));
            }}
            rotuloVazio="Escolha o que vale"
            opcoes={Object.entries(RESOLUCOES_ESCOLHIVEIS).map(([valor, rotulo]) => ({ valor, rotulo }))}
            idDoErro={erros.resolucao ? `${base}-resolucao-erro` : undefined}
          />
        </Campo>
        <div className="campo-linha">
        <Campo id={`${base}-justificativa`} rotulo="Por quê" erro={erros.justificativa}>
          <textarea
            id={`${base}-justificativa`}
            className="controle w-full"
            rows={4}
            value={justificativa}
            {...(erros.justificativa ? { 'aria-invalid': true, 'aria-describedby': `${base}-justificativa-erro` } : {})}
            onChange={(e) => {
              setJustificativa(e.target.value);
              setErros((atual) => ({ ...atual, justificativa: null }));
            }}
            onBlur={(e) => {
              const mensagem = mensagemDaJustificativa(e.target.value);
              setErros((atual) => ({ ...atual, justificativa: mensagem }));
            }}
          />
        </Campo>
        </div>
        {/* "opcional" ao lado do rótulo, pelo `Campo` (R01, p.28), e não entre parênteses no texto. */}
        <Campo id={`${base}-evidencia`} rotulo="Evidência" opcional>
          <input
            id={`${base}-evidencia`}
            type="text"
            className="controle w-full"
            placeholder="Boletim, e-mail, número da folha"
            value={evidencia}
            onChange={(e) => {
              setEvidencia(e.target.value);
            }}
          />
        </Campo>
        {recusa === null ? null : (
          <div role="alert" className="campo-linha flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
            <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
          </div>
        )}
      </form>
    );

  return (
    <PainelDetalhe
      /* Identidade da p.19: o código pequeno em monoespaçada acima, e o
         título é o que se trata aqui — o tipo da divergência. */
      codigo={d.equipamento}
      titulo={rotuloDoTipo(d.tipo)}
      subtitulo={diaDe(d.dia)}
      marcadores={<span className="txt-corpo text-tinta">{d.situacao === 'aberta' ? 'Aberta' : 'Resolvida'}</span>}
      dadosChave={
        <>
          <DadoChave rotulo="Horímetro">{horas(d.horasMotor)}</DadoChave>
          <DadoChave rotulo="Turno apontado">{horas(d.horasTurno)}</DadoChave>
          <DadoChave rotulo="Produção apontada">{horas(d.horasProducao)}</DadoChave>
          <DadoChave rotulo="Reconhecida">{horas(d.horasReconhecidas)}</DadoChave>
          {d.horasTelemetria !== undefined && d.horasTelemetria !== null ? (
            <DadoChave rotulo="Motor pela telemetria">{horas(d.horasTelemetria)}</DadoChave>
          ) : null}
          <DadoChave rotulo="Diferença">
            <Medida valor={diferenca(d.diferencaH)} unidade="h" />
          </DadoChave>
          {/* QUEM RESPONDE pela divergência. O resumo da IA conta "N sem
              responsável" desde a OPE 03, e até hoje a tela não dizia de quem
              era nenhuma — nem tinha por onde atribuir. */}
          <DadoChave rotulo="Responsável">{d.responsavel ?? 'Ninguém ainda'}</DadoChave>
        </>
      }
      abas={[
        {
          id: 'tratamento',
          rotulo: d.situacao === 'aberta' ? 'Resolver' : 'Resolução',
          conteudo: (
            <div className="flex flex-col gap-4">
              <p className="txt-apoio text-pretty text-tinta-secundaria">{explicacao}</p>
              {tratamento}
            </div>
          ),
        },
      ]}
      acoes={
        d.situacao === 'aberta' && podeConferir
          ? [
              <Botao key="resolver" nivel="primario" type="submit" form={`${base}-form`} disabled={resolver.isPending}>
                {resolver.isPending ? 'Gravando…' : 'Resolver divergência'}
              </Botao>,
            ]
          : []
      }
      aoFechar={aoFechar}
    />
  );
}

/**
 * A tolerância é da EMPRESA (ADR-006: o que diverge entre clientes é dado).
 * Mudar exige motivo, e a trilha guarda o valor anterior. A fila não se refaz
 * sozinha: depois de mudar, reapure o período.
 */
function ToleranciaDaEmpresa({ atual }: { readonly atual: number | null }) {
  const base = useId();
  const gravar = useGravarTolerancia();
  /* Mudar a tolerância é `turno.gerir`; quem não tem lê o valor de hoje. */
  const sessao = useSessao();
  const podeMudar = (sessao.data?.permissoes ?? []).includes('turno.gerir');
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  /* UM ERRO POR CAMPO: antes um só `erro` servia aos dois, e a recusa do
     Motivo aparecia sob "Nova tolerância" (p.16: a mensagem fica abaixo do
     campo a que se refere). */
  const [erroValor, setErroValor] = useState<string | null>(null);
  const [erroMotivo, setErroMotivo] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);
  const [gravada, setGravada] = useState(false);

  const mensagemDoValor = (texto: string) => {
    const numero = interpretarNumero(texto);
    return numero === null || numero < 0 || numero > 24
      ? 'Informe a tolerância em horas, entre 0 e 24. Exemplo: 0,5.'
      : null;
  };
  const mensagemDoMotivo = (texto: string) =>
    texto.trim().length < 3 ? 'Diga por que a tolerância está mudando.' : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const numero = interpretarNumero(valor);
    const achadoValor = mensagemDoValor(valor);
    const achadoMotivo = mensagemDoMotivo(motivo);
    setErroValor(achadoValor);
    setErroMotivo(achadoMotivo);
    if (achadoValor !== null) {
      document.getElementById(`${base}-valor`)?.focus();
      return;
    }
    if (achadoMotivo !== null) {
      document.getElementById(`${base}-motivo`)?.focus();
      return;
    }
    if (numero === null) return;
    setRecusa(null);
    gravar.mutate(
      { toleranciaH: numero, motivo: motivo.trim() },
      {
        onSuccess: () => {
          setValor('');
          setMotivo('');
          setGravada(true);
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  if (!podeMudar) {
    return (
      <section className="cartao flex flex-col gap-1 p-6">
        <h2 className="txt-secao text-tinta">Tolerância da conciliação</h2>
        {atual === null ? (
          <Carregando oQue="Carregando a tolerância da empresa" className="flex max-w-[68ch] flex-col gap-2 py-1">
            <Barra className="h-3 w-full" />
          </Carregando>
        ) : (
          <p className="txt-apoio max-w-[68ch] text-pretty text-tinta-secundaria">
            {`Hoje: ${formatarDecimal(atual, 2)} h. Diferença até esse valor não abre divergência. Quem muda a tolerância é o perfil Administrador.`}
          </p>
        )}
      </section>
    );
  }

  return (
    <form onSubmit={enviar} className="cartao formulario-grade p-6">
      <div className="campo-linha flex flex-col gap-1">
        <h2 className="txt-secao text-tinta">Tolerância da conciliação</h2>
        {atual === null ? (
          /* Esqueleto na altura das duas linhas do texto (p.32), e não uma
             frase de "carregando" que troca de tamanho quando o valor chega. */
          <Carregando oQue="Carregando a tolerância da empresa" className="flex max-w-[68ch] flex-col gap-2 py-1">
            <Barra className="h-3 w-full" />
            <Barra className="h-3 w-1/2" />
          </Carregando>
        ) : (
          <p className="txt-apoio max-w-[68ch] text-pretty text-tinta-secundaria">
            {`Hoje: ${formatarDecimal(atual, 2)} h. Diferença até esse valor não abre divergência. Depois de mudar, reapure o período para a fila refletir o valor novo.`}
          </p>
        )}
      </div>
      <Campo id={`${base}-valor`} rotulo="Nova tolerância (h)" erro={erroValor}>
        <input
          id={`${base}-valor`}
          type="text"
          inputMode="decimal"
          className="controle w-full"
          placeholder="0,5"
          value={valor}
          {...(erroValor ? { 'aria-invalid': true, 'aria-describedby': `${base}-valor-erro` } : {})}
          onChange={(e) => {
            setValor(e.target.value);
            setErroValor(null);
            setGravada(false);
          }}
          onBlur={(e) => {
            setErroValor(mensagemDoValor(e.target.value));
          }}
        />
      </Campo>
      <div className="campo-largo">
        <Campo id={`${base}-motivo`} rotulo="Motivo" erro={erroMotivo}>
          <input
            id={`${base}-motivo`}
            type="text"
            className="controle w-full"
            placeholder="Contrato aceita 1 h por dia"
            value={motivo}
            {...(erroMotivo ? { 'aria-invalid': true, 'aria-describedby': `${base}-motivo-erro` } : {})}
            onChange={(e) => {
              setMotivo(e.target.value);
              setErroMotivo(null);
            }}
            onBlur={(e) => {
              setErroMotivo(mensagemDoMotivo(e.target.value));
            }}
          />
        </Campo>
      </div>
      {gravada ? (
        <p role="status" className="txt-apoio campo-linha text-tinta">
          Tolerância gravada. Reapure o período para a fila usar o valor novo.
        </p>
      ) : null}
      {recusa === null ? null : (
        <div role="alert" className="campo-linha flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      )}
      {/* Secundário de propósito: o laranja desta tela é "Resolver divergência". */}
      <div className="barra-de-acoes campo-linha">
        <Botao nivel="secundario" type="submit" disabled={gravar.isPending}>
          {gravar.isPending ? 'Gravando…' : 'Gravar tolerância'}
        </Botao>
      </div>
    </form>
  );
}
